import { Languages, Plus, Scale, Settings as SettingsIcon } from 'lucide-react'
import { SettingsPanel } from './SettingsPanel'
import type { ChatSettings } from '../hooks/useChat'
import { useCurrentUser } from '../hooks/useCurrentUser'
import type { User } from '../lib/types'
import { useI18n } from '../lib/i18n'

interface Props {
  settings: ChatSettings
  onSettingsChange: (s: ChatSettings) => void
  onNewChat: () => void
  historyLen: number
  isStreaming: boolean
}

export function Sidebar({
  settings,
  onSettingsChange,
  onNewChat,
  historyLen,
  isStreaming,
}: Props) {
  const { s } = useI18n()
  const pairs = (historyLen / 2) | 0
  const { user, loading: userLoading } = useCurrentUser()

  return (
    <aside className="w-64 bg-brand-100 border-r border-brand-200 flex flex-col h-screen flex-shrink-0">
      <div className="px-4 py-4 border-b border-brand-200">
        <div className="flex items-center gap-2 mb-4">
          <Scale className="w-6 h-6 text-brand-700 flex-shrink-0" />
          <h1 className="text-base font-bold text-brand-800 tracking-tight flex-1 truncate">
            {s.appName}
          </h1>
          <LanguageToggle />
        </div>
        <button
          type="button"
          onClick={onNewChat}
          className="w-full rounded-lg bg-white hover:bg-brand-50 border border-brand-200 px-3 py-2 text-sm flex items-center gap-2 transition-colors text-brand-700 font-medium"
        >
          <Plus className="w-4 h-4" />
          {s.newChat}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="flex items-center gap-1.5 text-[11px] font-medium text-brand-500 mb-3 uppercase tracking-wide">
          <SettingsIcon className="w-3 h-3" />
          {s.settings}
        </div>
        <SettingsPanel
          settings={settings}
          onChange={onSettingsChange}
          disabled={isStreaming}
        />
      </div>

      <UserCard user={user} loading={userLoading} />

      <div className="px-4 py-2 text-[11px] text-brand-400 border-t border-brand-200 font-mono">
        <div className="flex items-center justify-between">
          <span>{s.historyTurns(pairs)}</span>
          {isStreaming && (
            <span className="text-brand-700 animate-pulse">{s.streaming}</span>
          )}
        </div>
      </div>
    </aside>
  )
}

function UserCard({
  user,
  loading,
}: {
  user: User | null
  loading: boolean
}) {
  if (loading) {
    return (
      <div className="px-4 py-3 border-t border-brand-200">
        <div className="flex items-center gap-2 animate-pulse">
          <div className="w-8 h-8 rounded-full bg-brand-200" />
          <div className="flex-1 space-y-1">
            <div className="h-2.5 bg-brand-200 rounded w-2/3" />
            <div className="h-2 bg-brand-100 rounded w-1/2" />
          </div>
        </div>
      </div>
    )
  }
  if (!user) {
    return (
      <div className="px-4 py-3 border-t border-brand-200 text-[11px] text-brand-400">
        — not signed in —
      </div>
    )
  }
  // Build a 1-2 char initials avatar.  For Chinese names take the last
  // glyph (姓氏取最后一字感觉怪 — actually for "张律师" we want "张",
  // for "John Doe" we want "JD").  Heuristic: if the first char is a
  // CJK glyph use just it; otherwise take the first letter of each
  // whitespace-separated token.
  const initials = computeInitials(user.display_name)
  return (
    <div className="px-4 py-3 border-t border-brand-200">
      <div className="flex items-center gap-2 min-w-0">
        <div
          className="w-8 h-8 rounded-full bg-brand-700 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0"
          aria-hidden
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-xs font-medium text-brand-800 truncate"
            title={user.display_name}
          >
            {user.display_name}
          </div>
          <div
            className="text-[10px] text-brand-500 truncate"
            title={user.firm_name ?? user.email ?? user.user_id}
          >
            {user.firm_name ?? user.email ?? user.user_id}
          </div>
        </div>
      </div>
    </div>
  )
}

function computeInitials(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return '?'
  // CJK Unified Ideographs + extension A — single-glyph avatar for
  // Chinese/Japanese/Korean names.
  if (/[\u3400-\u9fff]/.test(trimmed[0])) {
    return trimmed[0]
  }
  const parts = trimmed.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0][0].toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function LanguageToggle() {
  const { lang, setLang, s } = useI18n()
  const next = lang === 'en' ? 'zh' : 'en'
  return (
    <button
      type="button"
      onClick={() => setLang(next)}
      title={s.toggleLangTitle}
      aria-label={s.toggleLangTitle}
      className="flex-shrink-0 flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-mono text-brand-500 hover:text-brand-800 hover:bg-white border border-brand-200 transition-colors"
    >
      <Languages className="w-3 h-3" />
      <span>{lang === 'en' ? 'EN' : '中'}</span>
    </button>
  )
}
